import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Table,
  IconButton,
} from '@mui/joy'
import {
  ArrowLeft as ArrowBackIcon,
  DownloadSimple as DownloadIcon,
  ArrowsClockwise as RefreshIcon,
  ChartBar as AssessmentIcon,
  Image as ImageIcon,
} from '@phosphor-icons/react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '../../lib/api'

interface Detection {
  id: number
  detection_id: string
  category: string
  category_detail: {
    name: string
    display_name: string
    severity: string
    color_hex: string
  }
  confidence: number
  bbox: number[]
  center_point: { x: number; y: number }
  pixel_area: number
  estimated_area_sq_ft: number | null
}

interface Analysis {
  id: number
  analysis_id: string
  job: number | null
  job_detail: any
  photo: number
  photo_detail: {
    id: number
    name: string
    image: string
    thumbnail_url: string
  }
  status: string
  model_version: string
  runtime_info: any
  created_at: string
  started_at: string | null
  completed_at: string | null
  processing_time_ms: number | null
  severity_score: number | null
  summary: { [key: string]: any }
  detection_overlay_url: string | null
  heatmap_overlay_url: string | null
  report_url: string | null
  error_message: string | null
  detections: Detection[]
  detection_count: number
}

export const AnalysisDetail: React.FC = () => {
  const { analysisId } = useParams<{ analysisId: string }>()
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState(0)

  // Fetch analysis details
  const { data: analysis, isLoading, error, refetch } = useQuery({
    queryKey: ['hawkeye-analysis', analysisId],
    queryFn: async () => {
      const response = await api.get<Analysis>(`/api/hawkeye/analyses/${analysisId}/`)
      return response.data
    },
    refetchInterval: (query) => {
      // Poll while processing
      const data = query.state.data;
      if (data && (data.status === 'processing' || data.status === 'queued')) {
        return 2000 // 2 seconds
      }
      return false
    },
  })

  // Reanalyze mutation
  const reanalyze = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/api/hawkeye/analyses/${analysisId}/reanalyze/`, {})
      return response.data
    },
    onSuccess: (data) => {
      navigate(`/analysis/${(data as any)?.id || analysisId}`)
    },
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success'
      case 'processing': return 'primary'
      case 'failed': return 'danger'
      case 'queued': return 'warning'
      default: return 'neutral'
    }
  }

  const getSeverityColor = (score: number | null) => {
    if (score === null) return 'neutral'
    if (score < 0.3) return 'success'
    if (score < 0.6) return 'warning'
    return 'danger'
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !analysis) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert color="danger">Failed to load analysis details</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="plain"
            startDecorator={<ArrowBackIcon />}
            onClick={() => navigate('/history')}
          >
            Back
          </Button>
          <Typography level="h2">Analysis Details</Typography>
          <Chip color={getStatusColor(analysis.status)} variant="soft">
            {analysis.status}
          </Chip>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {analysis.status === 'processing' && (
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          )}
          {analysis.status === 'completed' && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<RefreshIcon />}
                onClick={() => reanalyze.mutate()}
                loading={reanalyze.isPending}
              >
                Reanalyze
              </Button>
              {analysis.report_url && (
                <Button
                  startDecorator={<DownloadIcon />}
                  onClick={() => analysis.report_url && window.open(analysis.report_url, '_blank')}
                >
                  Download Report
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {analysis.error_message && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {analysis.error_message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Overview Cards */}
        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Severity Score
              </Typography>
              <Typography level="h3">
                {analysis.severity_score !== null ? (analysis.severity_score * 100).toFixed(0) : 'N/A'}%
              </Typography>
              <Chip
                size="sm"
                color={getSeverityColor(analysis.severity_score)}
                variant="soft"
              >
                {analysis.severity_score !== null
                  ? analysis.severity_score < 0.3 ? 'Low'
                  : analysis.severity_score < 0.6 ? 'Medium'
                  : 'High'
                  : 'N/A'}
              </Chip>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Total Detections
              </Typography>
              <Typography level="h3">{analysis.detection_count}</Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                Damage instances found
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Processing Time
              </Typography>
              <Typography level="h3">
                {analysis.processing_time_ms !== null
                  ? `${(analysis.processing_time_ms / 1000).toFixed(1)}s`
                  : 'N/A'}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                Analysis duration
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Model Version
              </Typography>
              <Typography level="title-md">{analysis.model_version}</Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                {new Date(analysis.created_at).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Main Content */}
        <Grid xs={12}>
          <Card>
            <Tabs value={selectedTab} onChange={(_, value) => setSelectedTab(value as number)}>
              <TabList>
                <Tab>
                  <ImageIcon style={{ marginRight: 8 }} />
                  Results
                </Tab>
                <Tab>
                  <AssessmentIcon style={{ marginRight: 8 }} />
                  Detections
                </Tab>
                <Tab>Summary</Tab>
                <Tab>Technical Info</Tab>
              </TabList>

              <TabPanel value={0}>
                <Box sx={{ p: 2 }}>
                  {analysis.detection_overlay_url ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <img
                        src={analysis.detection_overlay_url}
                        alt="Detection overlay"
                        style={{ maxWidth: '100%', height: 'auto' }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography level="title-md" sx={{ mb: 2 }}>Original Image</Typography>
                        <img
                          src={analysis.photo_detail.image}
                          alt="Original"
                          style={{ maxWidth: '100%', height: 'auto' }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </TabPanel>

              <TabPanel value={1}>
                <Box sx={{ p: 2 }}>
                  <Table>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Confidence</th>
                        <th>Location</th>
                        <th>Area (px)</th>
                        <th>Severity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.detections.map((detection) => (
                        <tr key={detection.id}>
                          <td>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: detection.category_detail.color_hex,
                                }}
                              />
                              {detection.category_detail.display_name}
                            </Box>
                          </td>
                          <td>{(detection.confidence * 100).toFixed(1)}%</td>
                          <td>
                            [{detection.bbox[0]}, {detection.bbox[1]}]
                          </td>
                          <td>{detection.pixel_area}</td>
                          <td>
                            <Chip size="sm" variant="soft">
                              {detection.category_detail.severity}
                            </Chip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Box>
              </TabPanel>

              <TabPanel value={2}>
                <Box sx={{ p: 2 }}>
                  <Typography level="title-md" sx={{ mb: 2 }}>Detection Summary</Typography>
                  <Grid container spacing={2}>
                    {Object.entries(analysis.summary).map(([category, data]) => (
                      <Grid key={category} xs={12} sm={6} md={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography level="title-sm">
                              {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Typography>
                            <Typography level="h4">{(data as any).count}</Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                              Avg confidence: {((data as any).avg_confidence * 100).toFixed(1)}%
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </TabPanel>

              <TabPanel value={3}>
                <Box sx={{ p: 2 }}>
                  <Typography level="title-md" sx={{ mb: 2 }}>Runtime Information</Typography>
                  <pre style={{ backgroundColor: 'var(--joy-palette-background-level1)', padding: '16px', borderRadius: '8px', overflow: 'auto' }}>
                    {JSON.stringify(analysis.runtime_info, null, 2)}
                  </pre>
                </Box>
              </TabPanel>
            </Tabs>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}